import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Navbar } from "@/components/Navbar";
import { isAdminSession } from "@/lib/adminAuth";

// Los montos van en dolares y con el formato de EE. UU., sea cual sea el idioma
// del navegador.
const usd = (n) => Number(n || 0).toLocaleString("en-US");
import { 
  ArrowLeft, 
  Download, 
  Printer,
  DollarSign,
  TrendingUp,
  Target,
  AlertTriangle,
  CheckCircle,
  Lock,
  MapPin,
  Star,
  Share2,
  Globe,
  BarChart3,
  ShoppingCart,
  Cpu,
  Phone,
  MessageSquare,
  Map,
  Link as LinkIcon
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const sectionIcons = {
  listings: MapPin,
  reviews: Star,
  social: Share2,
  website: Globe,
  seo: TrendingUp,
  advertising: BarChart3,
  ecommerce: ShoppingCart,
  tech_stack: Cpu,
  voice: Phone,
  ai_chatbot: MessageSquare,
  mapping: Map,
  linking: LinkIcon
};

const sectionLabels = {
  listings: "Listings",
  reviews: "Reviews",
  social: "Social Media",
  website: "Website",
  seo: "SEO",
  advertising: "Advertising",
  ecommerce: "Ecommerce",
  tech_stack: "Tech Stack",
  voice: "Voice/Phone",
  ai_chatbot: "AI Chatbot",
  mapping: "Mapping",
  linking: "Linking"
};

// Pricing data for each service
const servicePricing = {
  "Listing Management & Optimization Package": { monthly: 299, setup: 199, value: 750 },
  "Review Generation & Reputation Management": { monthly: 349, setup: 149, value: 900 },
  "Social Media Management & Content Creation": { monthly: 499, setup: 299, value: 1200 },
  "Website Redesign & Optimization": { monthly: 199, setup: 2499, value: 600 },
  "SEO & Local Search Optimization": { monthly: 599, setup: 399, value: 1500 },
  "Paid Advertising Management (PPC/Social)": { monthly: 449, setup: 199, value: 1100 },
  "E-commerce Setup & Optimization": { monthly: 349, setup: 999, value: 850 },
  "Technology Integration & Automation": { monthly: 249, setup: 499, value: 600 },
  "AI Phone Receptionist & Call Handling": { monthly: 199, setup: 99, value: 500 },
  "AI Chatbot & Automated Response System": { monthly: 149, setup: 299, value: 400 },
  "Google Maps Optimization & Local SEO": { monthly: 249, setup: 149, value: 600 },
  "Link Building & Social Profile Integration": { monthly: 399, setup: 199, value: 950 }
};

const OpportunityCard = ({ section, data, index }) => {
  const Icon = sectionIcons[section] || Target;
  const label = sectionLabels[section] || section;
  const pricing = servicePricing[data.sales_opportunity] || { monthly: 299, setup: 199, value: 750 };
  
  const priorityColor = data.competitive_gap > 30 ? "border-red-300 bg-red-50" :
                        data.competitive_gap > 15 ? "border-orange-300 bg-orange-50" :
                        "border-yellow-300 bg-yellow-50";
  
  const priorityLabel = data.competitive_gap > 30 ? "HIGH PRIORITY" :
                        data.competitive_gap > 15 ? "MEDIUM PRIORITY" :
                        "OPPORTUNITY";
  
  const priorityBadge = data.competitive_gap > 30 ? "bg-red-100 text-red-700" :
                        data.competitive_gap > 15 ? "bg-orange-100 text-orange-700" :
                        "bg-yellow-100 text-yellow-700";

  return (
    <Card className={`border-2 ${priorityColor}`} data-testid={`opportunity-${section}`}>
      <CardContent className="p-5">
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-white flex items-center justify-center shadow-sm">
              <Icon className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <h3 className="font-semibold text-slate-900">{label}</h3>
              <Badge className={priorityBadge}>{priorityLabel}</Badge>
            </div>
          </div>
          <div className="text-right">
            <div className="text-2xl font-bold text-slate-900">{data.score}%</div>
            <div className="text-xs text-slate-500">Current Score</div>
          </div>
        </div>
        
        {/* Gap Analysis */}
        <div className="grid grid-cols-3 gap-3 mb-4 text-center">
          <div className="bg-white rounded-lg p-2">
            <div className="text-lg font-bold text-red-600">-{data.competitive_gap}</div>
            <div className="text-xs text-slate-500">Gap to Leader</div>
          </div>
          <div className="bg-white rounded-lg p-2">
            <div className="text-lg font-bold text-slate-700">{data.local_benchmark}%</div>
            <div className="text-xs text-slate-500">Local Avg</div>
          </div>
          <div className="bg-white rounded-lg p-2">
            <div className="text-lg font-bold text-slate-700">{data.national_benchmark}%</div>
            <div className="text-xs text-slate-500">National Avg</div>
          </div>
        </div>
        
        {/* Service Recommendation */}
        <div className="bg-white rounded-lg p-4 border border-slate-200">
          <div className="text-sm font-semibold text-slate-900 mb-3">
            {data.sales_opportunity}
          </div>
          
          <div className="grid grid-cols-3 gap-2 text-center">
            <div>
              <div className="text-lg font-bold text-blue-600">${usd(pricing.monthly)}</div>
              <div className="text-xs text-slate-500">/month</div>
            </div>
            <div>
              <div className="text-lg font-bold text-slate-600">${usd(pricing.setup)}</div>
              <div className="text-xs text-slate-500">Setup</div>
            </div>
            <div>
              <div className="text-lg font-bold text-green-600">${usd(pricing.value)}</div>
              <div className="text-xs text-slate-500">Est. Value</div>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default function PriceReport() {
  const { reportId } = useParams();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchReport();
  }, [reportId]);

  const fetchReport = async () => {
    try {
      const response = await axios.get(`${API}/reports/${reportId}`);
      setReport(response.data);
    } catch (error) {
      console.error("Failed to fetch report:", error);
    }
    setLoading(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-100">
        <Navbar />
        <div className="pt-20 flex items-center justify-center">
          <div className="animate-spin w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full"></div>
        </div>
      </div>
    );
  }

  if (!report) {
    return (
      <div className="min-h-screen bg-slate-100">
        <Navbar />
        <div className="pt-20 max-w-4xl mx-auto px-4 py-12 text-center">
          <h1 className="text-2xl font-bold text-slate-900 mb-4">Report Not Found</h1>
          {isAdminSession() ? (
            <Link to="/reports">
              <Button>View All Reports</Button>
            </Link>
          ) : (
            <Link to="/lookup">
              <Button>Analyze a Business</Button>
            </Link>
          )}
        </div>
      </div>
    );
  }

  // Get all sections with sales opportunities
  const allSections = ["listings", "reviews", "social", "website", "seo", "advertising", 
                       "ecommerce", "tech_stack", "voice", "ai_chatbot", "mapping", "linking"];
  
  const opportunities = allSections
    .filter(s => report[s] && report[s].sales_opportunity && report[s].competitive_gap > 0)
    .map(s => ({ section: s, data: report[s] }))
    .sort((a, b) => b.data.competitive_gap - a.data.competitive_gap);

  // Calculate totals
  const totalMonthly = opportunities.reduce((sum, opp) => {
    const pricing = servicePricing[opp.data.sales_opportunity] || { monthly: 299 };
    return sum + pricing.monthly;
  }, 0);

  const totalSetup = opportunities.reduce((sum, opp) => {
    const pricing = servicePricing[opp.data.sales_opportunity] || { setup: 199 };
    return sum + pricing.setup;
  }, 0);

  const totalValue = opportunities.reduce((sum, opp) => {
    const pricing = servicePricing[opp.data.sales_opportunity] || { value: 750 };
    return sum + pricing.value;
  }, 0);

  const highPriority = opportunities.filter(o => o.data.competitive_gap > 30);
  const mediumPriority = opportunities.filter(o => o.data.competitive_gap > 15 && o.data.competitive_gap <= 30);
  const lowPriority = opportunities.filter(o => o.data.competitive_gap <= 15);

  return (
    <div className="min-h-screen bg-slate-100" data-testid="price-report">
      <Navbar />
      
      <div className="pt-20 pb-12">
        <div className="max-w-6xl mx-auto px-4">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div>
              <Link to={`/report/${reportId}`} className="flex items-center gap-2 text-blue-600 hover:text-blue-700 mb-2">
                <ArrowLeft className="w-4 h-4" />
                Back to Report
              </Link>
              <h1 className="text-3xl font-bold text-slate-900">Sales Opportunity Report</h1>
              <p className="text-slate-600">{report.business_name} • Internal Use Only</p>
            </div>
            <div className="flex items-center gap-2">
              <Badge className="bg-red-100 text-red-700 border border-red-200">
                <Lock className="w-3 h-3 mr-1" />
                INTERNAL ONLY
              </Badge>
              <Button variant="outline" onClick={() => window.print()}>
                <Printer className="w-4 h-4 mr-2" />
                Print
              </Button>
            </div>
          </div>

          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
            <Card className="border-slate-200">
              <CardContent className="p-4 text-center">
                <DollarSign className="w-8 h-8 mx-auto text-blue-600 mb-2" />
                <div className="text-3xl font-bold text-slate-900">${usd(totalMonthly)}</div>
                <div className="text-sm text-slate-500">Monthly Revenue</div>
              </CardContent>
            </Card>
            <Card className="border-slate-200">
              <CardContent className="p-4 text-center">
                <Target className="w-8 h-8 mx-auto text-green-600 mb-2" />
                <div className="text-3xl font-bold text-slate-900">${usd(totalSetup)}</div>
                <div className="text-sm text-slate-500">Setup Revenue</div>
              </CardContent>
            </Card>
            <Card className="border-slate-200">
              <CardContent className="p-4 text-center">
                <TrendingUp className="w-8 h-8 mx-auto text-purple-600 mb-2" />
                <div className="text-3xl font-bold text-slate-900">${usd(totalValue)}</div>
                <div className="text-sm text-slate-500">Est. Monthly Value</div>
              </CardContent>
            </Card>
            <Card className="border-slate-200">
              <CardContent className="p-4 text-center">
                <AlertTriangle className="w-8 h-8 mx-auto text-red-600 mb-2" />
                <div className="text-3xl font-bold text-slate-900">{opportunities.length}</div>
                <div className="text-sm text-slate-500">Opportunities</div>
              </CardContent>
            </Card>
          </div>

          {/* ROI Summary */}
          <Card className="border-green-200 bg-green-50 mb-8">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-semibold text-green-900 mb-1">Projected ROI for Client</h3>
                  <p className="text-green-700">If all services are purchased</p>
                </div>
                <div className="text-right">
                  <div className="text-4xl font-bold text-green-600">
                    {((totalValue / totalMonthly) || 0).toFixed(1)}x
                  </div>
                  <div className="text-sm text-green-700">Monthly ROI</div>
                </div>
              </div>
              <div className="mt-4 pt-4 border-t border-green-200 grid grid-cols-3 gap-4 text-center">
                <div>
                  <div className="text-2xl font-bold text-green-800">${usd(totalMonthly * 12)}</div>
                  <div className="text-xs text-green-600">Annual Contract Value</div>
                </div>
                <div>
                  <div className="text-2xl font-bold text-green-800">${usd(totalMonthly * 12 + totalSetup)}</div>
                  <div className="text-xs text-green-600">First Year Total</div>
                </div>
                <div>
                  <div className="text-2xl font-bold text-green-800">${usd(totalValue * 12)}</div>
                  <div className="text-xs text-green-600">Est. Annual Value to Client</div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* High Priority Opportunities */}
          {highPriority.length > 0 && (
            <div className="mb-8">
              <h2 className="text-xl font-bold text-red-700 mb-4 flex items-center gap-2">
                <AlertTriangle className="w-5 h-5" />
                High Priority ({highPriority.length})
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {highPriority.map((opp, i) => (
                  <OpportunityCard key={opp.section} section={opp.section} data={opp.data} index={i} />
                ))}
              </div>
            </div>
          )}

          {/* Medium Priority Opportunities */}
          {mediumPriority.length > 0 && (
            <div className="mb-8">
              <h2 className="text-xl font-bold text-orange-700 mb-4 flex items-center gap-2">
                <Target className="w-5 h-5" />
                Medium Priority ({mediumPriority.length})
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {mediumPriority.map((opp, i) => (
                  <OpportunityCard key={opp.section} section={opp.section} data={opp.data} index={i} />
                ))}
              </div>
            </div>
          )}

          {/* Low Priority Opportunities */}
          {lowPriority.length > 0 && (
            <div className="mb-8">
              <h2 className="text-xl font-bold text-yellow-700 mb-4 flex items-center gap-2">
                <CheckCircle className="w-5 h-5" />
                Additional Opportunities ({lowPriority.length})
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {lowPriority.map((opp, i) => (
                  <OpportunityCard key={opp.section} section={opp.section} data={opp.data} index={i} />
                ))}
              </div>
            </div>
          )}

          {/* Quick Pitch */}
          <Card className="border-blue-200 bg-blue-50">
            <CardHeader>
              <CardTitle className="text-blue-900">Quick Pitch Script</CardTitle>
            </CardHeader>
            <CardContent className="text-blue-800">
              <p className="mb-4">
                "Based on our analysis of <strong>{report.business_name}</strong>, we've identified 
                <strong> {opportunities.length} areas</strong> where you're falling behind your local competition.
              </p>
              {highPriority.length > 0 && (
                <p className="mb-4">
                  Your biggest gaps are in <strong>{highPriority.map(o => sectionLabels[o.section]).join(", ")}</strong>. 
                  You're currently <strong>{highPriority[0]?.data.competitive_gap} points behind</strong> the local leader 
                  in {sectionLabels[highPriority[0]?.section]}.
                </p>
              )}
              <p>
                We can help you close these gaps with our services. A full engagement would be 
                <strong> ${usd(totalMonthly)}/month</strong> plus <strong>${usd(totalSetup)} setup</strong>, 
                with an estimated return of <strong>${usd(totalValue)}/month</strong> in value – 
                that's a <strong>{((totalValue / totalMonthly) || 0).toFixed(1)}x ROI</strong>.
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
